import React from 'react';
import { ModuleConfig } from '../../types/ModuleConfig';

const MyBuild = React.lazy(() => import('./MyBuild'));
const BuildPage = React.lazy(() => import('./BuildPage'));
const ManageBuild = React.lazy(() => import('./ManageBuild'));
const BuildUpdates = React.lazy(() => import('./BuildUpdates'));
const TeamProfiles = React.lazy(() => import('./TeamProfiles'));
const TeamProfilePage = React.lazy(() => import('./TeamProfilePage'));
const EmailTemplates = React.lazy(() => import('./EmailTemplates'));

/**
 * Build portal (2026-10-09) — the client's boutique view of their house build, and the team's side: post updates, invite
 * clients, keep in touch with every client at least every 28 days, team profiles. Permissions come from the backend manifest
 * (business-builder-backend/src/modules/build-portal/permissions.ts): `view` = the client (BUILD_PORTAL_CLIENT) or the
 * team; `staff`/`create`/`admin` = the Command Centre's levels. Arrays here stay EMPTY (SSOT).
 */
const buildPortalModuleConfig: ModuleConfig = {
  id: 'build-portal',
  name: 'Build Portal',
  description: "The client's view of their house build — progress, stages, updates, reports and their team",
  version: '0.3.0',
  enabled: true,
  category: 'business',
  order: 18,
  icon: '🏡',
  color: '#141C49',
  routes: [
    { path: '/my-build', component: MyBuild, permissionLevel: 'view', useLayout: true, layoutOptions: { noContainer: true } },
    { path: '/my-build/updates', component: BuildUpdates, permissionLevel: 'staff', useLayout: true, layoutOptions: { noContainer: true } },
    { path: '/my-build/team', component: TeamProfiles, permissionLevel: 'staff', useLayout: true, layoutOptions: { noContainer: true } },
    { path: '/my-build/templates', component: EmailTemplates, permissionLevel: 'staff', useLayout: true, layoutOptions: { noContainer: true } },
    { path: '/my-build/team/:key', component: TeamProfilePage, permissionLevel: 'view', useLayout: true, layoutOptions: { noContainer: true } },
    { path: '/my-build/:dealId/manage', component: ManageBuild, permissionLevel: 'staff', useLayout: true, layoutOptions: { noContainer: true } },
    { path: '/my-build/:dealId', component: BuildPage, permissionLevel: 'view', useLayout: true, layoutOptions: { noContainer: true } },
  ],
  navigation: [
    { label: 'My build', path: '/my-build', icon: '🏡', permissionLevel: 'view' },
    { label: 'Client updates', path: '/my-build/updates', icon: '🕒', permissionLevel: 'staff' },
    { label: 'Email templates', path: '/my-build/templates', icon: '✉️', permissionLevel: 'staff' },
    { label: 'Team profiles', path: '/my-build/team', icon: '👥', permissionLevel: 'staff' },
  ],
  permissions: { view: [], create: [], edit: [], delete: [] },
  changelog: [
    { version: '0.3.0', releaseDate: '2026-10-19', title: 'Email templates page', features: ['✉️ Email templates: every stage\'s builder and client email plus the photo request on one page, with standard or edited shown, edited in place'] },
    {
      version: '0.2.0',
      releaseDate: '2026-10-09',
      title: 'Build portal',
      features: [
        '🏡 The client page: progress bar, the journey as a winding road, the story of updates, the report, their team',
        '✍️ Manage a build: post an update (photos, report, move the stage), invite the client, see who was told and who read it',
        '🕒 Client updates: every build — red after 28 days without contact',
        '👥 Team profiles: who the client meets, and how to reach them',
      ],
    },
    { version: '0.1.0', releaseDate: '2026-10-08', title: 'Build portal demo', features: ['✨ Demo client page over a real deal'] },
  ],
};

export default buildPortalModuleConfig;
